import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import ExcelJS from "exceljs";
import switchTipoNodo from "./switchTipoNodo";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import TooltipPrime from "../components/PrimeComponents/TooltipPrime";

dayjs.extend(utc);
dayjs.extend(tz);


const headersTable = (hoja, celda, columna, valor, alineacion, fuente, ancho) => {
    hoja.getCell(celda).value = valor;
    hoja.getCell(celda).alignment = alineacion;
    hoja.getCell(celda).font = fuente;
    hoja.getColumn(columna).width = ancho;
};

const bodyTable = (hoja, rango, valor, alineacion, fuente, borde) => {
    hoja.getCell(rango).value = valor;
    hoja.getCell(rango).alignment = alineacion;
    hoja.getCell(rango).font = fuente;
    hoja.getCell(rango).border = borde;
};

const cellTable = (hoja, rango, valor, alineacion, fuente) => {
    hoja.getCell(rango).value = valor;
    hoja.getCell(rango).alignment = alineacion;
    hoja.getCell(rango).font = fuente;
};

const resolveNodeOffline = (farm, offlineNodeType, t, i18n) => {
    // sort offline nodes in ascending order according fecha_ultimo_dato
    const sortedOfflineNodes = farm.nodos?.filter(el => el.atrasado && el.fecha_ultimo_dato).sort((a, b) => {
        return (a.fecha_ultimo_dato < b.fecha_ultimo_dato) ? -1 : ((a.fecha_ultimo_dato > b.fecha_ultimo_dato) ? 1 : 0);
    });
    let date;
    if (offlineNodeType === "oldest") date = sortedOfflineNodes?.[0]?.fecha_ultimo_dato;
    else if (offlineNodeType === "recent") date = sortedOfflineNodes?.[sortedOfflineNodes?.length - 1]?.fecha_ultimo_dato;
    return date ? formatReportDates(date, i18n.language) : "";
};

async function generarExcel(data, t, i18n, dataAccountRef) {
    const workbook = new ExcelJS.Workbook();

    // Crear hoja "account" (1)
    const accountSheet = workbook.addWorksheet(t("farms.farmTable.razonSocial"));
    const font = { bold: true, name: "Arial" };
    const fontArial = { name: "Arial" };
    const alignment = {
        vertical: "center",
        horizontal: "center",
        wrapText: true,
    };
    const borderTopBottom = {
        top: { style: "thin" },
        bottom: { style: "thin" },
    };
    const borderAll = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
    };
    accountSheet.getCell("A2").value = t("accounts.statusAccount");
    accountSheet.mergeCells("A2:F2");
    accountSheet.getCell("A2").alignment = alignment;
    accountSheet.getCell("A2").font = font;

    headersTable(accountSheet, "A3", "A", t("farms.farmTable.razonSocial"), alignment, font, 16);
    headersTable(accountSheet, "B3", "B", t("farms.farms"), alignment, font, 30);
    headersTable(accountSheet, "C3", "C", t("node.cantidadNodos"), alignment, font, 10);
    headersTable(accountSheet, "D3", "D", t("node.nodesOffline"), alignment, font, 9);
    headersTable(accountSheet, "E3", "E", t("node.oldestOffline"), alignment, font, 16);
    headersTable(accountSheet, "F3", "F", t("node.recentOffline"), alignment, font, 16);

    const farms = [];

    // Crear hoja "Summary" (2)
    const summarySheet = workbook.addWorksheet(t("farms.summary"));
    summarySheet.getColumn("A").width = 28;
    const sensorName = [];
    summarySheet.getCell("B1").value = t("farms.actuator.actuator");
    summarySheet.getCell("B1").font = { name: "Arial"};
    const cellRange = summarySheet.getCell("A1:Q1");
    const alignmentOptions = {
        vertical: "middle",
        horizontal: "center",
    };
    const cellStyles = {
        font: { bold: true, name: "Arial" },
        alignment: {
            vertical: alignmentOptions.vertical,
            horizontal: alignmentOptions.horizontal,
            wrapText: true,
        },
    };

    summarySheet.eachRow({ min: cellRange.top, max: cellRange.bottom }, (row) => {
        row.eachCell((cell) => {
            Object.assign(cell, cellStyles);
        });
    });

    summarySheet.getRow(cellRange.top).eachCell((cell, colNumber) => {
        const sensor = sensorName[colNumber - 2];
        cell.value = sensor;
    });

    data.forEach((farm, index) => {
    /* HOJA 1 */
        farms.push(farm.name);
        const row = index + 4;
        const rangeB = `B${row}`;
        const rangeC = `C${row}`;
        const rangeD = `D${row}`;
        const rangeE = `E${row}`;
        const rangeF = `F${row}`;

        bodyTable(accountSheet, rangeB, farm.name, alignment, fontArial, borderTopBottom);
        bodyTable(accountSheet, rangeC, farm.totalNodos, alignment, fontArial, borderTopBottom);
        bodyTable(accountSheet, rangeD, farm.nodosAtrasados, alignment, fontArial, borderTopBottom);
        bodyTable(accountSheet, rangeE, resolveNodeOffline(farm, "oldest", t, i18n) ?? "-", alignment, fontArial, borderTopBottom);
        bodyTable(accountSheet, rangeF, resolveNodeOffline(farm, "recent", t, i18n) ?? "-", alignment, fontArial, borderTopBottom);

        /* HOJA 2 */
        farm.data_sensores = [];
        const rowB = index + 2;
        const rangeA = `A${rowB}`;

        summarySheet.getCell(rangeA).value = farm.name;
        summarySheet.getCell(rangeA).alignment = {
            vertical: "middle",
            horizontal: "left",
        };
        summarySheet.getColumn("A").border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
        };
        summarySheet.getCell(rangeA).font = font;

        const sumData = {};
        let sumaCantidades = 0;
        farm.nodos?.forEach((nodo) => {
            nodo.data?.tipoActuador?.forEach((actuador) => {
                sumaCantidades += actuador.cantidad;
            });
            if (nodo.data) {
                const arrData = [];
                nodo.data.tipoActuador?.forEach((actuador) => {
                    const tipoAc = t(`tipo.actuador.${actuador.tipo}`);
                    let subTipo;
                    actuador.sub_tipo ? (subTipo = t(`tipo.asociacion_ha.${actuador.sub_tipo}`, "")) : (subTipo = "-");
                    actuador.nombre = `${tipoAc} - ${subTipo}`;
                    arrData.push({ nombre: actuador.nombre, cantidad: actuador.cantidad });
                });
                const contador = {};
                nodo.data.tipoSensor?.forEach((sensor) => {
                    const tipoSen = sensor.tipo[i18n.language];
                    !contador[tipoSen] ? (contador[tipoSen] = 1) : contador[tipoSen]++;
                    if (!sensorName.includes(tipoSen)) {
                        sensorName.push(tipoSen);
                    }
                });
                for (const tipoSen in contador) {
                    arrData.push({ nombre: tipoSen, cantidad: contador[tipoSen] });
                    const existingSensor = farm.data_sensores.find(
                        (sensor) => sensor.nombre === tipoSen
                    );
                    if (existingSensor) {
                        existingSensor.cantidad += contador[tipoSen];
                    } else {
                        farm.data_sensores.push({ nombre: tipoSen, cantidad: contador[tipoSen] });
                    }
                }

                nodo.data_procesada = arrData;
                if (nodo.data_procesada) {
                    nodo.data_procesada.forEach((data) => {
                        const { nombre, cantidad } = data;
                        if (sumData[nombre]) {
                            sumData[nombre] += cantidad;
                        } else {
                            sumData[nombre] = cantidad;
                        }
                    });
                }
            }
        });

        const rangeB2 = `B${rowB}`;
        bodyTable(summarySheet, rangeB2, sumaCantidades === 0 ? "" : sumaCantidades, { vertical: "middle", horizontal: "left" }, fontArial, borderAll);
        summarySheet.getColumn("B").width = 13;

    // columna C en adelante, desde fila 2
    });

    const rangeA = `A4:A${farms.length + 3}`;
    accountSheet.mergeCells(rangeA);
    cellTable(accountSheet, rangeA, dataAccountRef.razon_social, { vertical: "middle", horizontal: "center"}, fontArial);
    const totalRow = farms.length + 4;
    accountSheet.getCell(`B${totalRow}`).value = { formula: `SUM(B4:B${totalRow - 1})` };

    cellTable(accountSheet, `A${totalRow}`, "Total", alignment, fontArial);
    cellTable(accountSheet, `B${totalRow}`, { formula: `${farms.length}` }, alignment, font);
    cellTable(accountSheet, `C${totalRow}`, { formula: `SUM(C4:C${totalRow - 1})` }, alignment, font);
    cellTable(accountSheet, `D${totalRow}`, { formula: `SUM(D4:D${totalRow - 1})` }, alignment, font);
    const ratioRow = farms.length + 5;
    cellTable(accountSheet, `D${ratioRow}`, { formula: `D${totalRow}/C${totalRow}` }, alignment, font);

    accountSheet.getCell(`D${ratioRow}`).numFmt = "0.00%";
    accountSheet.getCell(`D${ratioRow}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" },
    };

    const startRow = 4;
    const endRow = startRow + farms.length - 1;
    const range = `A${startRow}:F${endRow}`;
    accountSheet.eachRow({ min: startRow, max: endRow }, (row) => {
        row.eachCell((cell) => {
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
            };
        });
    });

    accountSheet.getCell(range).border = {
        outline: true,
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
    };

    // Hoja 2
    // let lastColumn;
    // const startRowSummary = 2;
    // const endRowSummary = startRowSummary + farms.length - 1;

    // Renderiza desde Columna C hasta tantos nombres sensores tenga, en la fila 1
    sensorName.sort();
    sensorName.forEach((nombre, index) => {
        const quotient = Math.floor(index / 24);
        const remainder = index % 24;
        let firstChar = String.fromCharCode(67 + remainder);
        let secondChar = "";
        if (quotient > 0) {
            secondChar = String.fromCharCode(65 + quotient - 1);
            firstChar = String.fromCharCode(65 + remainder);
        }
        const column = secondChar + firstChar;
        // lastColumn = column;
        const range = `${column}1`;
        summarySheet.getCell(range).value = nombre;
        summarySheet.getCell(range).alignment = {
            vertical: "middle",
            horizontal: "center",
        };
        summarySheet.getCell(range).font = { name: "Arial"};
        summarySheet.getColumn(column).width = 13;
    });

    // Es necesario hacer un nuevo forEach para que coincidan los valores con los nombres de la columnas. Dentro del primer data.forEach no coinciden.
    data.forEach((farm, index) => {
    // columna C en adelante, desde fila 2 (valores - cantidades)
        farm.data_sensores?.forEach((sensor) => {
            const columnIndex = sensorName.findIndex((nombre) => nombre === sensor.nombre);
            if (columnIndex !== -1) {
                const column = columnIndex + 3;
                let columnName = "";
                if (column <= 26) {
                    columnName = String.fromCharCode(65 + column - 1);
                } else {
                    const firstCharIndex = Math.floor((column - 1) / 26) - 1;
                    const secondCharIndex = (column - 1) % 26;
                    columnName =
            String.fromCharCode(65 + firstCharIndex) +
            String.fromCharCode(65 + secondCharIndex);
                }
                const row = index + 2;
                const range = `${columnName}${row}`;
                summarySheet.getCell(range).value = sensor.cantidad;
                summarySheet.getCell(range).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                summarySheet.getCell(range).font = { name: "Arial" };
                summarySheet.getColumn(columnName).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                };
            }
        });
    });

    // const rango = `C${startRowSummary}:${lastColumn}${endRowSummary}`;
    // const rango = `A1:${lastColumn}${endRowSummary}`;

    summarySheet.getRow(1).font = { bold: true, name: "Arial" };
    summarySheet.getColumn("A").font = { bold: true, name: "Arial" };

    let hasValue = false;
    summarySheet.eachRow({ min: cellRange.top, max: cellRange.bottom }, (row) => {
        row.eachCell((cell) => {
            hasValue = cell.value !== null && cell.value !== undefined;
            if (hasValue) {
                cell.alignment = {
                    vertical: alignmentOptions.vertical,
                    horizontal: alignmentOptions.horizontal,
                    wrapText: true,
                };
            }
        });
    });

    /* Crear hojas de campo */
    data.forEach((farm) => {
        const nameFarm = workbook.addWorksheet(`${farm.name}`);
        nameFarm.getCell("A1").value = `${farm.name}`; // traducirlo
        nameFarm.mergeCells("A1:G1");
        nameFarm.getCell("A1").alignment = {
            vertical: "middle",
            horizontal: "center",
        };
        nameFarm.getCell("A1").font = { bold: true };

        nameFarm.getColumn("A").width = 3;
        nameFarm.getColumn("B").width = 33;
        nameFarm.getColumn("C").width = 15;
        nameFarm.getColumn("D").width = 7;
        nameFarm.getColumn("E").width = 25;
        nameFarm.getColumn("F").width = 28;
        nameFarm.getColumn("G").width = 8;
        nameFarm.getColumn("I").width = 33;
        nameFarm.getColumn("J").width = 28;

        const alignment = {
            vertical: "center",
            horizontal: "center",
            wrapText: true,
        };
        const font = { bold: true, size: 11 };

        nameFarm.getCell("A2").value = "#"; // traducirlo
        nameFarm.getCell("A2").alignment = alignment;
        nameFarm.getCell("A2").font = font;

        nameFarm.getCell("B2").value = t("node.name");
        nameFarm.getCell("B2").alignment = alignment;
        nameFarm.getCell("B2").font = font;

        nameFarm.getCell("C2").value = t("node.tipoHeader");
        nameFarm.getCell("C2").alignment = alignment;
        nameFarm.getCell("C2").font = font;

        nameFarm.getCell("D2").value = t("node.online");
        nameFarm.getCell("D2").alignment = alignment;
        nameFarm.getCell("D2").font = font;

        nameFarm.getCell("E2").value = t("node.ultimoDato");
        nameFarm.getCell("E2").alignment = alignment;
        nameFarm.getCell("E2").font = font;

        nameFarm.getCell("F2").value = t("node.connectionType");
        nameFarm.getCell("F2").alignment = alignment;
        nameFarm.getCell("F2").font = font;

        nameFarm.getCell("G2").value = t("node.quantity");
        nameFarm.getCell("G2").alignment = alignment;
        nameFarm.getCell("G2").font = font;

        const sumData = {};

        let row = 3;
        let rowApi = 3;
        let nodeNumeral = 1;
        farm.nodos?.forEach((nodo, index) => {
            if (nodo.data) {
                const arrData = [];
                const cantidadActuadores = nodo.data.tipoActuador?.reduce((res, cur) => res + cur.cantidad, 0);
                if (cantidadActuadores){
                    arrData.push({
                        nombre: t("farms.actuator.actuator"),
                        cantidad: cantidadActuadores,
                    });
                }
                const contador = {};
                nodo.data.tipoSensor?.forEach((sensor) => {
                    const tipoSen = sensor.tipo[i18n.language];
                    !contador[tipoSen] ? (contador[tipoSen] = 1) : contador[tipoSen]++;
                });
                for (const tipoSen in contador) {
                    arrData.push({ nombre: tipoSen, cantidad: contador[tipoSen] });
                }

                nodo.data_procesada = arrData;
                if (!farm.data) {
                    farm.data = [];
                }
                // const sumData = {};
                if (nodo.data_procesada) {
                    nodo.data_procesada.forEach((data) => {
                        const { nombre, cantidad } = data;
                        if (sumData[nombre]) {
                            sumData[nombre] += cantidad;
                        } else {
                            sumData[nombre] = cantidad;
                        }
                    });
                }

                const rangeB = `B${row}`; // nombre
                const rangeC = `C${row}`; // tipo
                const rangeD = `D${row}`; // online
                const rangeE = `E${row}`; // ultimo dato

                fillCell(nameFarm, `A${row}`, nodeNumeral);
                nodeNumeral++;

                nameFarm.getCell(rangeB).value = nodo.nombre;
                nameFarm.getCell(rangeB).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeB).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeC).value = !nodo.is_gw
                    ? switchTipoNodo(nodo.tipo)
                    : `${switchTipoNodo(nodo.tipo)} Gateway`;
                nameFarm.getCell(rangeC).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeC).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeD).value = nodo.atrasado
                    ? ""
                    : t("general.answerYes");
                nameFarm.getCell(rangeD).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeD).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeE).value = nodo.fecha_ultimo_dato
                    ? formatReportDates(nodo.fecha_ultimo_dato, i18n.language)
                    : "";
                nameFarm.getCell(rangeE).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeE).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                if (arrData.length > 0) {
                    const connectionRowStart = row;
                    const connectionRowEnd = row + arrData.length - 1;
                    arrData.forEach((conexion, i) => {
                        const connectionRangeF = `F${row}`;
                        const connectionRangeG = `G${row}`;

                        nameFarm.getCell(connectionRangeF).value = conexion.nombre;
                        nameFarm.getCell(connectionRangeF).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        nameFarm.getCell(connectionRangeF).border = {
                            top: { style: "thin" },
                            bottom: { style: "thin" },
                        };

                        nameFarm.getCell(connectionRangeG).value = conexion.cantidad;
                        nameFarm.getCell(connectionRangeG).alignment = {
                            vertical: "middle",
                            horizontal: "center",
                        };
                        nameFarm.getCell(connectionRangeG).border = {
                            top: { style: "thin" },
                            bottom: { style: "thin" },
                        };
                        row++;
                    });
                    nameFarm.mergeCells(`A${connectionRowStart}:A${connectionRowEnd}`);
                    nameFarm.mergeCells(`B${connectionRowStart}:B${connectionRowEnd}`);
                    nameFarm.mergeCells(`C${connectionRowStart}:C${connectionRowEnd}`);
                    nameFarm.mergeCells(`D${connectionRowStart}:D${connectionRowEnd}`);
                    nameFarm.mergeCells(`E${connectionRowStart}:E${connectionRowEnd}`);
                    return;
                } else {
                    nameFarm.getCell(`F${row}`).border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                    };
                    nameFarm.getCell(`G${row}`).border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                    };
                }
                row++;
            } else {
                // handle api nodes table
                if (nodo.is_external){
                    if (rowApi === 3) {
                        fillCell(nameFarm, "I2:J2", "API", true);
                        nameFarm.mergeCells("I2:J2");
                    }
                    fillCell(nameFarm, `I${rowApi}`, nodo.nombre);
                    if (nodo.sensores?.length > 0) {
                        const connectionRowStart = rowApi;
                        const connectionRowEnd = rowApi + nodo.sensores.length - 1;
                        nodo.sensores.forEach((sensor) => {
                            fillCell(nameFarm, `J${rowApi}`, sensor.tipo[i18n.language]);
                            rowApi++;
                        });
                        nameFarm.mergeCells(`I${connectionRowStart}:I${connectionRowEnd}`);
                        return;
                    } else {
                        // just add border
                        fillCell(nameFarm, `J${rowApi}`, "");
                    }
                    rowApi++;
                    return;
                }
                const rangeB = `B${row}`; // nombre
                const rangeC = `C${row}`; // tipo
                const rangeD = `D${row}`; // online
                const rangeE = `E${row}`; // ultimo dato
                const rangeF = `F${row}`; // tipo/s
                const rangeG = `G${row}`; // cantidad/es

                nameFarm.getCell(rangeB).value = nodo.nombre;
                nameFarm.getCell(rangeB).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeB).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeC).value = !nodo.is_gw
                    ? switchTipoNodo(nodo.tipo)
                    : `${switchTipoNodo(nodo.tipo)} Gateway`;
                nameFarm.getCell(rangeC).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeC).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeD).value = nodo.atrasado
                    ? ""
                    : t("general.answerYes");
                nameFarm.getCell(rangeD).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeD).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeE).value = nodo.fecha_ultimo_dato
                    ? formatReportDates(nodo.fecha_ultimo_dato, i18n.language)
                    : "";
                nameFarm.getCell(rangeE).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeE).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeF).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeF).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };

                nameFarm.getCell(rangeG).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };
                nameFarm.getCell(rangeG).border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                };
                row++;
            }
        });

        // total nodos offline
        fillCell(nameFarm, `B${row}`, t("node.nodesOffline"), true);
        nameFarm.mergeCells(`B${row}:C${row}`);
        fillCell(nameFarm, `D${row}`, farm.nodosAtrasados, true);

        farm.data_procesada = sumData;
        const sortedSummary = Object.entries(farm.data_procesada).sort((a,b) => b[1] - a[1]);
        // summary table in specific farm sheet
        rowApi++; // newline before summary table
        fillCell(nameFarm, `I${rowApi}`, t("node.connectionType"), true);
        fillCell(nameFarm, `J${rowApi}`, t("node.quantity"), true);
        rowApi++;
        sortedSummary.forEach((el) => {
            fillCell(nameFarm, `I${rowApi}`, el[0]); // tipo conexion
            fillCell(nameFarm, `J${rowApi}`, el[1]); // cantidad total tipo conexion
            rowApi++;
        });
        fillCell(nameFarm, `I${rowApi}`, t("forms.total"), true);
        fillCell(nameFarm, `J${rowApi}`, sortedSummary.reduce((res, cur) => res + cur[1],0));

    });

    //
    const buffer = await workbook.xlsx.writeBuffer();
    return buffer;
}

const formatReportDates = (isoDate, locale) => {
    const nDate = dayjs.utc(isoDate).toDate();
    const fmt = new Intl.DateTimeFormat(locale || "en", {
        year: "numeric", month: "numeric", day: "numeric",
        hour: "numeric", minute: "numeric",
        hour12: false, timeZone: "utc"
    });
    return `${fmt.format(nDate)}`.replace(",", "");
};

const GeneratorExcel = ({ data, t, i18n, dataAccountRef }) => {
    // console.log(data);
    const handleDownload = () => {
        generarExcel(data, t, i18n, dataAccountRef)
            .then((buffer) => {
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `${dataAccountRef.razon_social}.xlsx`);
                link.click();

                URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Error al generar el archivo de Excel:", error);
            });
    };

    return (
        <>
            <TooltipPrime id="generate-excel-download"  content={t("accounts.descargar")} position="bottom" >
                <FontAwesomeIcon
                    icon={faDownload}
                    onClick={handleDownload}
                    size="1x"
                    style={{ cursor: "pointer" }}
                />
            </TooltipPrime>
        </>
    );
};

const fillCell = (sheet, cell, value, isBold = false) => {
    sheet.getCell(cell).value = value;
    sheet.getCell(cell).alignment = {
        vertical: "middle",
        horizontal: "center",
    };
    sheet.getCell(cell).border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
    };
    if (isBold) sheet.getCell(cell).font = { ...sheet.getCell(cell).font, bold: true };
};

export { GeneratorExcel };
